<template>
    <div
        v-if="data && data.name"
        :class="`report-block report-block--type-${type} report-block--size-${size}`"
    >
        <div class="report-block__top">
            <template v-if="isSingle">
                <span v-if="data.lang">{{ data.lang }}</span>
                <span v-if="data.fileType">{{ data.fileType }} <span v-if="data.fileSize">&nbsp;({{ data.fileSize }})</span></span>
            </template>
            <span v-if="data.date">{{ data.date }}</span>
        </div>
        <p
            class="report-block__title u-bottom-margin--xs"
            :class="{'report-block__title--hovered' : isHovered}"
        >
            <vue-raw :raw="data.name"/>
        </p>
        <div class="report-block__content">
            <component
                :is="defineReportComponent(getCoverLink)"
                :href="getCoverLink"
                :target="getIsOpenInNewTab"
                :style="`background-image: url(${data.image})`"
                :class="{'report-block__image--hovered' : isHovered}"
                class="report-block__image"
                @click="handlerTrackGoal(goals.goalNameBlockImage)"
            />
            <div class="report-block__links-list">
                <div v-if="data.fileLink" class="report-block__link-wrapper">
                    <z-button
                        class="report-block__link report-block__link--pdf"
                        tag="a"
                        :href="data.fileLink"
                        target="_blank"
                        kind="secondary"
                        rounded
                        size="s"
                        @click="handlerTrackGoal(goals.goalNameBtnPdf)"
                        @mouseover="isHovered = true"
                        @mouseleave="isHovered = false"
                    >
                        <span class="u-bold">{{ getFileFormat(data.fileLink) }}</span>
                    </z-button>
                    <span v-if="showFileSize && data.pdfSize" class="report-block__link-size">
                            {{ data.pdfSize }}
                    </span>
                </div>
                <z-button
                    v-if="data.onlineLink"
                    class="report-block__link report-block__link--online"
                    tag="a"
                    :href="data.onlineLink"
                    target="_blank"
                    kind="secondary"
                    size="s"
                    rounded
                    @click="handlerTrackGoal(goals.goalNameBtnOnline)"
                    @mouseover="isHovered = true"
                    @mouseleave="isHovered = false"
                >
                    <span v-html="text.onSite"></span>
                </z-button>
                <div v-if="data.brochure" class="report-block__link-wrapper">
                    <z-button
                        class="report-block__link report-block__link--brochure"
                        tag="a"
                        :href="data.brochure"
                        target="_blank"
                        kind="secondary"
                        size="s"
                        rounded
                        @mouseover="isHovered = true"
                        @mouseleave="isHovered = false"
                    >
                        <span v-html="text.brochure"></span>
                    </z-button>
                    <span v-if="showFileSize && data.brochureSize" class="report-block__link-size">
                            {{ data.brochureSize }}
                        </span>
                </div>
                <z-button
                    v-if="data.learnMoreLink"
                    :href="data.learnMoreLink"
                    :target="getIsOpenInNewTab"
                    tag="a"
                    class="report-block__link"
                    kind="secondary"
                    size="s"
                    @click="handlerTrackGoal(goals.goalNameBtnDetails)"
                    @mouseover="isHovered = true"
                    @mouseleave="isHovered = false"
                >
                    {{ text.learnMore }}
                </z-button>
                <div v-if="data.taggedFileLink && !data.onlineLink" class="report-block__link-wrapper">
                    <z-button
                        class="report-block__link report-block__link--xbrl"
                        tag="a"
                        :href="data.taggedFileLink"
                        target="_blank"
                        kind="secondary"
                        size="s"
                        rounded
                        @click="handlerTrackGoal(goals.goalNameBtnXbrl)"
                        @mouseover="isHovered = true"
                        @mouseleave="isHovered = false"
                    >
                        <span class="u-bold">XBRL</span>
                    </z-button>
                    <span v-if="showFileSize && data.taggedFileSize " class="report-block__link-size">
                            {{ data.taggedFileSize }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {localize} from '@/utils/i18n'
import {trackGoal} from '@/utils/analytics'

export default {
    name: 'report-block',
    props: {
        data: {
            type: Object,
            default: () => ({
                lang: '',
                fileType: '',
                image: '',
                name: '',
                fileLink: '',
                onlineLink: '',
                brochure: '',
                taggedFileLink: '',
                learnMoreLink: ''
            })
        },
        isTargetBlank: {
            type: Boolean,
            default: true
        },
        goals: {
            type: Object,
            default: () => ({
                goalNameBlockImage: '',
                goalNameBtnOnline: '',
                goalNameBtnPdf: '',
                goalNameBtnXbrl: '',
                goalNameBtnDetails: ''
            })
        },
        size: {
            type: String,
            default: 'm',
            validator: prop => ['l', 'm'].includes(prop)
        },
        type: {
            type: String,
            default: 'default',
            validator: prop => ['default', 'horizontal', 'presentation'].includes(prop)
        },
        showFileSize: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            isHovered: false,
            text: {
                onSite: localize({
                    ru: 'Онлайн',
                    en: 'Online'
                }),
                learnMore: localize({
                    ru: 'Подробнее',
                    en: 'Learn more'
                }),
                brochure: localize({
                    ru: 'Брошюра',
                    en: 'Brochure'
                }),
                source: localize({
                    ru: 'Посмотреть источник',
                    en: 'View source'
                }),
                save: localize({
                    ru: 'Сохранить PDF',
                    en: 'Save as PDF'
                })
            }
        }
    },
    computed: {
        getIsOpenInNewTab () {
            return this.isTargetBlank && '_blank'
        },
        getCoverLink () {
            return this.data.onlineLink || this.data.learnMoreLink
        },
        isSingle () {
            const { fileLink, taggedFileLink, brochure, onlineLink } = this.data
            const links = [fileLink, taggedFileLink, brochure, onlineLink]
            const filledLinks = links.filter(link => !!link)

            return filledLinks.length === 1
        }
    },
    methods: {
        defineReportComponent (link) {
            if (!link) {
                return 'div'
            } else {
                return 'a'
            }
        },
        handlerTrackGoal (goalName) {
            trackGoal(goalName)
        },
        getFileFormat (fileName) {
            const strArr = fileName.split('.')
            return strArr[strArr.length - 1].toUpperCase()
        }
    }
}
</script>

<style lang="scss">
.report-block {
    --image-height: 132%;
    $parent: &;
    $custom-transition: 0.4s ease-in-out;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: transform $custom-transition;
    transform-origin: top left;

    @include breakpoint(mobile) {
        max-width: 100%;
        margin-top: $token-spacers-xs;
    }

    &--size {
        &-l {
            --image-width: 243px;
        }
        &-m {
            --image-width: 170px;
        }
    }

    &--type {
        &-horizontal {
            --image-height: 56%;
            &.report-block--size-l {
                --image-width: 350px;
            }
            &.report-block--size-m {
                --image-width: 250px;
            }
        }
        &-presentation {
            &.report-block--size-l {
                --image-width: 320px;
            }
            &.report-block--size-m {
                --image-width: 217px;
            }
        }
    }

    &__top {
        display: flex;
        align-items: center;

        & span {
            display: flex;
            align-items: center;
            font-size: 14px;
            line-height: 1.4;
            color: $token-colors-gray-30;
            letter-spacing: 0.7px;
            text-transform: uppercase;
        }

        & span:not(:last-of-type) {
            margin-right: $token-spacers-3-xs;

            &:after {
                margin-left: $token-spacers-3-xs;
                content: '';
                width: 4px;
                height: 4px;
                display: inline-block;
                border-radius: 50%;
                background: $token-colors-gray-30;
            }
        }
    }

    &__title {
        max-width: calc(var(--image-width) + 104px);
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        font-weight: 600;
        line-height: 1.2;
        color: $token-colors-gray-60;
        transition: color $custom-transition;

        &--hovered {
            color: $token-colors-navy-blue;
        }
    }

    &__content {
        margin-top: auto;
        display: flex;
        gap: 16px;
    }

    &__image {
        position: relative;
        max-width: var(--image-width);
        width: 100%;
        flex-shrink: 0;
        background-size: auto 100%;
        background-repeat: no-repeat;
        background-position: 100% 0;
        border-radius: 8px 8px 8px 0px;
        box-shadow: 1.399px 2.798px 5px 2px #FFF, 0.6px 10px 20px 0px rgba(35, 64, 81, 0.20);
        transition: transform $custom-transition, box-shadow $custom-transition;

        &:before {
            padding-top: var(--image-height);
            content: '';
            width: 100%;
            background-repeat: no-repeat;
            background-size: cover;
            background-position: 50% 50%;
            display: block;
        }

        @include breakpoint(mobile) {
            max-width: 170px;
            width: var(--image-width);
        }

        &--hovered {
            box-shadow: 2px 2px 12px 0 #FFF, 4px 18px 40px 0 rgba(14, 93, 140, 0.40);
        }
    }

    &__links-list {
        display: flex;
        flex-direction: column;
        gap: $token-spacers-3-xs;
        transition: transform $custom-transition;
        transform-origin: left top;
    }

    &__link-wrapper {
        display: flex;
        align-items: center;
        gap: $token-spacers-3-xs;
    }

    &__link.z-button, &__link.z-button.z-button--size-s {
        padding: 10px 14px;
        font-size: 12px;
        font-weight: 600;
        border-radius: 20px;
        letter-spacing: 1.2px;
        background: transparent;
        transition: $custom-transition;

        span {
            font-size: 12px;
            display: flex;
            align-items: center;
        }

        &:hover {
            color: $token-colors-button-secondary-text-hover;
            border-color: $token-colors-button-secondary-text-hover;
            background-color: transparent;
            transform: unset;
        }
    }

    &__link-size {
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        letter-spacing: 0.7px;
        text-transform: uppercase;
        color: $token-colors-gray-30;
    }
}
</style>
